import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { GroupAdd, Add, Close, Warning, Person, FileCopy } from "@material-ui/icons";

import select from "../../../../utils/select";
import { assignCouncil } from "../../../../reducer/studentResearch/action";
import { filterCouncil } from "../../../../reducer/council/action";
import { notification } from "../../../../reducer/home/action";
import SectionCard from "../../utils/SectionCard";
import useDueWindow from "../../utils/useDueWindow";
import {
  COUNCIL_ROLES,
  buildDefaultMembers,
  groupMembersByRole,
  getRoleConfig,
  validateMembers,
} from "../../../../constants/councilRoles";
import {
  COUNCIL_TYPE_PROGRESS,
  COUNCIL_TYPE_FINAL,
} from "../../../../constants/projectStatus";

// CouncilForm (refactored 2026-05) — gán hội đồng cho 1 đề tài cụ thể.
//
// Schema mới: gửi BE { councilType, councilTemplateId?, members[] }. BE upsert
// student_research_council_assignments + snapshot members. Sửa template SAU
// không ảnh hưởng đề tài.
//
// Mode UX:
//   1. Áp dụng template: chọn từ dropdown → auto-fill members từ template,
//      vẫn có thể edit từng người trước khi save.
//   2. Nhập tay: bỏ chọn template → nhập members tự do.
//
// Window guard: nghiệm thu (final) phải nằm trong conferenceStart/End.
function CouncilForm(props) {
  const dispatch = useDispatch();
  const { values, setValues, detail, type = COUNCIL_TYPE_FINAL, handleClose } = props;

  const profileListRaw = useSelector((s) => select(s, "profileReducer", "list")) || [];
  const templateList = useSelector((s) => select(s, "councilReducer", "council")) || [];

  // Refactor 2026-05-21: loại GV hướng dẫn của chính đề tài khỏi danh sách
  // chọn thành viên HĐ — tránh xung đột lợi ích (GV hướng dẫn ngồi HĐ chấm
  // chính đề tài mình hướng dẫn).
  const profileList = useMemo(() => {
    const excludeIds = new Set(
      (detail?.instructors || [])
        .map((ins) => ins?.profileId)
        .filter((id) => id && id > 0)
    );
    if (excludeIds.size === 0) return profileListRaw;
    return profileListRaw.filter((p) => !excludeIds.has(p.id));
  }, [profileListRaw, detail?.instructors]);

  // Form state — derived from `values` prop nhưng tự manage members local.
  const selectedType = values?.type || type;

  // Load templates phù hợp loại hội đồng + năm học của đề tài.
  useEffect(() => {
    dispatch(
      filterCouncil({
        schoolYear: detail?.schoolYear,
        councilType: selectedType,
        module: "student",
      })
    );
  }, [dispatch, detail?.schoolYear, selectedType]);

  // Init members: nếu values chưa có members → dùng default skeleton.
  useEffect(() => {
    if (!values?.members || values.members.length === 0) {
      setValues((v) => ({
        ...(v || {}),
        type: v?.type || type,
        members: buildDefaultMembers(),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Window guard cho hội đồng nghiệm thu (final).
  const { check: checkDue } = useDueWindow(detail?.schoolYear);
  const conferenceWindow =
    selectedType === COUNCIL_TYPE_FINAL
      ? checkDue("conferenceStartTime", "conferenceEndTime")
      : { isOpen: true, reason: null };

  const grouped = useMemo(() => groupMembersByRole(values?.members || []), [values?.members]);
  const setMembers = (next) => setValues({ ...values, members: next });

  // Áp template: copy members từ template vào values + lưu councilTemplateId.
  const applyTemplate = (template) => {
    if (!template) {
      setValues({ ...values, councilTemplateId: 0, members: buildDefaultMembers() });
      return;
    }
    setValues({
      ...values,
      councilTemplateId: template.id,
      members: (template.members || []).map((m) => ({ ...m })),
    });
    dispatch(notification({ type: "info", message: `Đã áp dụng template "${template.name}". Có thể sửa từng thành viên trước khi lưu.` }));
  };

  // Member ops.
  const addMember = (roleKey) => {
    const cfg = getRoleConfig(roleKey);
    const same = (values?.members || []).filter((m) => m.roleKey === roleKey);
    if (same.length >= cfg.max) {
      dispatch(notification({ type: "warning", message: `Tối đa ${cfg.max} ${cfg.label.toLowerCase()}.` }));
      return;
    }
    const nextOrder = (same[same.length - 1]?.orderNum || 0) + 1;
    setMembers([
      ...values.members,
      { roleKey, orderNum: nextOrder, profileId: 0, name: "", degree: "", isPhenikaa: true },
    ]);
  };

  const removeMember = (roleKey, orderNum) => {
    const cfg = getRoleConfig(roleKey);
    const same = (values?.members || []).filter((m) => m.roleKey === roleKey);
    if (same.length <= cfg.min) {
      dispatch(notification({ type: "warning", message: `Cần ít nhất ${cfg.min} ${cfg.label.toLowerCase()}.` }));
      return;
    }
    setMembers(values.members.filter((m) => !(m.roleKey === roleKey && m.orderNum === orderNum)));
  };

  const updateMember = (roleKey, orderNum, patch) => {
    setMembers(
      values.members.map((m) =>
        m.roleKey === roleKey && m.orderNum === orderNum ? { ...m, ...patch } : m
      )
    );
  };

  const handleSubmit = async () => {
    if (!conferenceWindow.isOpen) {
      dispatch(notification({ type: "warning", message: conferenceWindow.reason }));
      return;
    }
    const reason = validateMembers(values?.members);
    if (reason) {
      dispatch(notification({ type: "warning", message: reason }));
      return;
    }
    const payload = {
      councilType: selectedType,
      councilTemplateId: values?.councilTemplateId || 0,
      members: values.members,
    };
    const ok = await dispatch(assignCouncil(detail.id, payload));
    if (ok && handleClose) handleClose();
  };

  return (
    <Box padding="16px 24px">
      {/* Banner cảnh báo window */}
      {!conferenceWindow.isOpen && (
        <Box
          marginBottom={2}
          padding="10px 16px"
          bgcolor="#fff3e0"
          border="solid 1px #fb8c00"
          borderRadius={4}
          display="flex"
          alignItems="center"
        >
          <Warning style={{ marginRight: 12, color: "#e65100" }} />
          <Typography variant="body2">
            <b>{conferenceWindow.reason}</b> Không thể lưu hội đồng nghiệm thu lúc này; chỉnh
            lại deadline ở "Cấu hình" trước khi tiếp tục.
          </Typography>
        </Box>
      )}

      <SectionCard icon={<GroupAdd />} title="Thông tin hội đồng" marginTop={0}>
        <Grid container spacing={2}>
          {/* Loại hội đồng + Áp dụng template */}
          <Grid item xs={4}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Loại hội đồng</b>
            </Typography>
            <Select
              fullWidth variant="outlined" margin="dense"
              value={selectedType}
              onChange={(e) => setValues({ ...values, type: e.target.value, councilTemplateId: 0 })}
            >
              <MenuItem value={COUNCIL_TYPE_FINAL}>Hội đồng nghiệm thu</MenuItem>
              <MenuItem value={COUNCIL_TYPE_PROGRESS}>Hội đồng duyệt báo cáo tiến độ</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={8}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Áp dụng template tiểu ban</b>
              <Tooltip title={`Chọn template để auto-fill thành viên. Vẫn có thể sửa từng người sau khi áp dụng. Template tạo ở trang "Hội đồng các tiểu ban".`}>
                <Box component="span" marginLeft={1} style={{ color: "#9e9e9e", cursor: "help" }}>?</Box>
              </Tooltip>
            </Typography>
            <Box display="flex" gridGap={8}>
              <Autocomplete
                options={templateList}
                getOptionLabel={(o) => o?.name || ""}
                value={templateList.find((t) => t.id === values?.councilTemplateId) || null}
                onChange={(_, val) => applyTemplate(val)}
                style={{ flexGrow: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params} size="small" variant="outlined"
                    placeholder={templateList.length === 0 ? "Chưa có template — tạo ở /nckh-sinh-vien/council" : "Chọn template..."}
                  />
                )}
              />
              {values?.councilTemplateId > 0 && (
                <Tooltip title="Bỏ template (chuyển sang nhập tay)">
                  <IconButton size="small" onClick={() => applyTemplate(null)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {values?.councilTemplateId > 0 && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "flex", alignItems: "center", gridGap: 4 }}>
                <FileCopy fontSize="inherit" /> Đang áp dụng template (snapshot lưu khi nhấn "Lưu hội đồng").
              </Typography>
            )}
          </Grid>

          {/* Members */}
          <Grid item xs={12}>
            <Box border="solid 1px #e0e0e0" borderRadius={4} padding={2} marginTop={1}>
              {COUNCIL_ROLES.map((role) => {
                const list = grouped[role.key] || [];
                return (
                  <Box key={role.key} marginBottom={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                      <Typography variant="body2">
                        <b>{role.label}</b>
                        <span style={{ color: "#9e9e9e", marginLeft: 8, fontSize: 12 }}>
                          ({list.length}{role.max > 1 ? `/${role.max}` : ""})
                        </span>
                      </Typography>
                      {role.max > role.min && (
                        <Button
                          size="small" variant="outlined" color="primary"
                          startIcon={<Add />}
                          disabled={list.length >= role.max}
                          onClick={() => addMember(role.key)}
                        >
                          Thêm {role.label.toLowerCase()}
                        </Button>
                      )}
                    </Box>
                    {list.map((m) => (
                      <MemberRow
                        key={`${m.roleKey}-${m.orderNum}`}
                        member={m}
                        role={role}
                        profileList={profileList}
                        canRemove={list.length > role.min}
                        onChange={(patch) => updateMember(m.roleKey, m.orderNum, patch)}
                        onRemove={() => removeMember(m.roleKey, m.orderNum)}
                      />
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </SectionCard>

      {/* Hidden submit button — modal footer "Lưu hội đồng" trigger handleSubmit
          via form id. */}
      <form id={props.id} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <input type="submit" hidden />
      </form>
    </Box>
  );
}

function MemberRow({ member, role, profileList, canRemove, onChange, onRemove }) {
  const isPhenikaa = member.isPhenikaa !== false;
  const selectedProfile = isPhenikaa
    ? profileList.find((p) => p.id === member.profileId) || null
    : null;

  return (
    <Box
      display="flex"
      gridGap={8}
      alignItems="center"
      padding="6px 8px"
      bgcolor="#fafafa"
      borderRadius={4}
      marginBottom={6}
      border="solid 1px #eee"
    >
      <Box width={150} flexShrink={0}>
        <FormControlLabel
          control={
            <Checkbox
              size="small" color="primary" checked={isPhenikaa}
              onChange={(e) =>
                onChange({
                  isPhenikaa: e.target.checked,
                  profileId: e.target.checked ? member.profileId : 0,
                })
              }
            />
          }
          label={<Typography variant="caption">GV trong trường</Typography>}
        />
      </Box>
      <Box flexGrow={1} minWidth={0}>
        {isPhenikaa ? (
          <Autocomplete
            options={profileList}
            getOptionLabel={(o) => o?.name || ""}
            value={selectedProfile}
            onChange={(_, val) =>
              onChange({
                profileId: val?.id || 0,
                name: val?.name || "",
                degree: val?.degree || "",
              })
            }
            renderInput={(params) => (
              <TextField {...params} size="small" variant="outlined" placeholder="Chọn GV" />
            )}
          />
        ) : (
          <TextField
            fullWidth size="small" variant="outlined"
            placeholder="Tên thành viên"
            value={member.name || ""}
            onChange={(e) => onChange({ name: e.target.value })}
            InputProps={{
              startAdornment: <Person fontSize="small" style={{ color: "#9e9e9e", marginRight: 6 }} />,
            }}
          />
        )}
      </Box>
      <Box width={140} flexShrink={0}>
        <TextField
          fullWidth size="small" variant="outlined"
          placeholder="Học hàm/vị"
          disabled={isPhenikaa}
          value={member.degree || ""}
          onChange={(e) => onChange({ degree: e.target.value })}
        />
      </Box>
      {canRemove ? (
        <Tooltip title={`Xóa ${role.label.toLowerCase()}`}>
          <IconButton size="small" onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Box width={28} />
      )}
    </Box>
  );
}

export default CouncilForm;
